import {
  TextField,
  Typography,
  Button,
  IconButton,
  Divider,
  Autocomplete,
  Snackbar,
  Alert,
  AlertTitle,
} from '@mui/material'
import './LineEdit.css'
import { quoteLineType } from '../types'
import { ArrowForward } from '@mui/icons-material'
import { CurrencyInput } from '../helper'
import { manufacturerProviders } from '../lists'
import { useState } from 'react'
import axios from 'axios'

export function LineEdit(props: {
  open: boolean
  onClose: () => void
  fullLineItems: (quoteLineType & { children: quoteLineType[] })[]
  setLineItems: (lineItems: (quoteLineType & { children: quoteLineType[] })[]) => void
  selectedLineItems?: quoteLineType[]
  selectedLineItem?: quoteLineType
  quoteId: string
  type: 'bulk' | 'single'
}) {
  const [updateLineItem, setUpdateLineItem] = useState<Partial<quoteLineType>>({} as Partial<quoteLineType>)
  const [noChangesSnackbar, setNoChangesSnackbar] = useState(false)
  const [bulkSnackbar, setBulkSnackbar] = useState<'success' | 'fail' | null>(null)
  const [singleSnackbar, setSingleSnackbar] = useState<'success' | 'fail' | null>(null)

  const handleSave = async () => {
    if (Object.keys(updateLineItem).length === 0) {
      setNoChangesSnackbar(true)
      return
    }
    if (updateLineItem.UnitPrice || updateLineItem.Qty) {
      updateLineItem.ExtPrice =
        (updateLineItem.UnitPrice ?? props.selectedLineItem?.UnitPrice ?? 0) *
        (updateLineItem.Qty ?? props.selectedLineItem?.Qty ?? 0)
    }
    if (props.type !== 'bulk') {
      const lineItemToUpdate = {
        ...updateLineItem,
        ...(props.selectedLineItem?.Bundle && { Bundle: props.selectedLineItem.Bundle }),
        QuoteId: props.selectedLineItem?.QuoteId,
      }
      axios
        .post(`/editLineItem/${props.selectedLineItem?.LineItemId}`, {
          lineItem: lineItemToUpdate,
          quoteId: props.quoteId,
        })
        .then(res => {
          props.setLineItems(res.data)
          props.onClose()
        })
        .catch(err => {
          setSingleSnackbar('fail')
          console.error(err)
        })
    }
    if (props.type === 'bulk') {
      if (!props.selectedLineItems) return
      await Promise.all(
        props.selectedLineItems.map(lineItem => {
          const lineItemToUpdate = {
            ...updateLineItem,
            ...(lineItem.Bundle && { Bundle: lineItem.Bundle }),
          }
          return axios.post(`/editLineItem/${lineItem.LineItemId}`, {
            lineItem: lineItemToUpdate,
            quoteId: props.quoteId,
          })
        }),
      )
        .then(res => {
          props.setLineItems(res[res.length - 1].data)
          props.onClose()
        })
        .catch(err => {
          setBulkSnackbar('fail')
          console.error(err)
        })
    }
  }

  return (
    <div className="quote-line-edit">
      <div className="quote-line-edit-header">
        <Typography variant="h6">Edit Quote Line Item</Typography>
        <IconButton onClick={() => props.onClose()}>
          <ArrowForward />
        </IconButton>
      </div>
      <Divider />
      {props.type === 'bulk' && props.selectedLineItems && (
        <Alert severity="info" className="quote-line-edit-bulk-alert">
          <AlertTitle>
            {props.selectedLineItems.length} item{props.selectedLineItems.length > 1 ? 's' : ''} selected for editing
          </AlertTitle>
          <Typography variant="body1">
            {props.selectedLineItems.length > 1 ? `All ${props.selectedLineItems.length} items` : 'This item'} will be
            updated to reflect any changes made.
          </Typography>
        </Alert>
      )}
      <div className="quote-line-edit-content">
        <TextField
          size="small"
          fullWidth
          label="Mfr Part #"
          defaultValue={props.type !== 'bulk' ? props.selectedLineItem?.MfrPartNumber ?? '' : ''}
          onChange={e => setUpdateLineItem(prev => ({ ...prev, MfrPartNumber: e.target.value }))}
        />
        <TextField
          size="small"
          fullWidth
          multiline
          label="Description"
          defaultValue={props.type !== 'bulk' ? props.selectedLineItem?.Description ?? '' : ''}
          onChange={e => setUpdateLineItem(prev => ({ ...prev, Description: e.target.value }))}
        />
        <div className="quote-line-edit-content-double-row">
          <Autocomplete
            size="small"
            fullWidth
            options={manufacturerProviders}
            defaultValue={props.type !== 'bulk' ? props.selectedLineItem?.Provider ?? '' : ''}
            onChange={(_, value) => setUpdateLineItem(prev => ({ ...prev, Provider: value as string }))}
            renderInput={params => <TextField {...params} label="Distributor" />}
          />
          <Autocomplete
            size="small"
            fullWidth
            options={manufacturerProviders}
            defaultValue={props.type !== 'bulk' ? props.selectedLineItem?.Manufacturer ?? '' : ''}
            onChange={(_, value) => setUpdateLineItem(prev => ({ ...prev, Manufacturer: value as string }))}
            renderInput={params => <TextField {...params} label="Manufacturer" />}
          />
        </div>
        <div className="quote-line-edit-content-quad-row">
          <CurrencyInput
            size="small"
            fullWidth
            label="Unit Cost"
            disabled={props.selectedLineItem?.LineNumber === -1}
            value={updateLineItem.UnitCost ?? props.selectedLineItem?.UnitCost ?? null}
            onChange={value =>
              setUpdateLineItem(prev => ({
                ...prev,
                UnitCost: value ?? 0,
                ExtCost: (value ?? 0) * (updateLineItem.Qty ?? props.selectedLineItem?.Qty ?? 0),
              }))
            }
          />
          <CurrencyInput
            size="small"
            fullWidth
            label="Unit Price"
            disabled={props.selectedLineItem?.LineNumber === -1}
            value={updateLineItem.UnitPrice ?? props.selectedLineItem?.UnitPrice ?? null}
            onChange={value => {
              const unitPrice = value ?? 0
              const unitCost = updateLineItem.UnitCost ?? props.selectedLineItem?.UnitCost ?? 0
              const totalFees = updateLineItem.TotalFees ?? props.selectedLineItem?.TotalFees ?? 0
              const creditCard = updateLineItem.CreditCard ?? props.selectedLineItem?.CreditCard ?? null
              const qty = updateLineItem.Qty ?? props.selectedLineItem?.Qty ?? 0

              const costWithFees = unitCost + totalFees + (creditCard ? unitCost * 0.03 : 0)
              const margin = unitPrice === 0 ? 0 : (1 - costWithFees / unitPrice) * 100

              setUpdateLineItem(prev => ({
                ...prev,
                UnitPrice: unitPrice,
                ExtPrice: unitPrice * qty,
                Margin: margin,
              }))
            }}
          />
          <TextField
            size="small"
            fullWidth
            type="number"
            label="Qty"
            value={updateLineItem.Qty ?? props.selectedLineItem?.Qty ?? null}
            onChange={e =>
              setUpdateLineItem(prev => ({
                ...prev,
                Qty: Number(e.target.value),
                ExtPrice: (updateLineItem.UnitPrice ?? props.selectedLineItem?.UnitPrice ?? 0) * Number(e.target.value),
                ExtCost: (updateLineItem.UnitCost ?? props.selectedLineItem?.UnitCost ?? 0) * Number(e.target.value),
              }))
            }
          />
          <CurrencyInput
            disabled
            size="small"
            fullWidth
            label="Ext Price"
            value={updateLineItem.ExtPrice ?? props.selectedLineItem?.ExtPrice ?? null}
            onChange={value => setUpdateLineItem(prev => ({ ...prev, ExtPrice: value ?? 0 }))}
          />
        </div>
        <Button
          onClick={async () => {
            await handleSave()
          }}
          variant="contained"
          color="primary"
        >
          Save
        </Button>
      </div>
      <Snackbar
        autoHideDuration={5000}
        open={noChangesSnackbar}
        onClose={() => setNoChangesSnackbar(false)}
        message="No changes to save"
      />
      <Snackbar
        autoHideDuration={5000}
        open={singleSnackbar === 'success'}
        onClose={() => setSingleSnackbar(null)}
        message="Line item updated successfully"
      />
      <Snackbar
        autoHideDuration={5000}
        open={singleSnackbar === 'fail'}
        onClose={() => setSingleSnackbar(null)}
        message="Error updating line item"
      />
      <Snackbar
        autoHideDuration={5000}
        open={bulkSnackbar === 'success'}
        onClose={() => setBulkSnackbar(null)}
        message="Line items updated successfully"
      />
      <Snackbar
        autoHideDuration={5000}
        open={bulkSnackbar === 'fail'}
        onClose={() => setBulkSnackbar(null)}
        message="Error updating line items"
      />
    </div>
  )
}
